import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Row, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions, adminActions } from '../../redux/_actions';
import UserForm from './userform';
import TableComponent from '../helpers/tablecomponent';
import { modeType } from '../../utilities/constants';
import UserDetail from './userdetail';
import { SearchInput, addBold, noResults } from '../helpers/search';
import { Loading, ScrollLoading } from '../helpers/loading';

function UserAdmin(props) {
  const { setIsForm, handleMode } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const { LIST, DETAIL, EDIT, DELETE, ADD } = modeType;
  const [mode, setMode] = useState(LIST);
  const [userId, setUserId] = useState(null);
  const alert = useSelector((state) => state.alert);
  const users = useSelector((state) => state.admin.users);
  const nextToken = useSelector((state) => state.admin.users.tokens.nextToken);
  const searchQuery = useSelector(
    (state) => state.admin.users.tokens.searchQuery
  );
  const searchToken = useSelector(
    (state) => state.admin.users.tokens.searchToken
  );

  const [searchTerm, setSearchTerm] = useState(searchQuery || '');
  const [tempSearchTerm, setTempSearchTerm] = useState(searchQuery || ''); // used for async holding of query
  const dispatch = useDispatch();
  const numUsers = users?.allIds?.length || 0;
  const isSearch = searchTerm !== '';
  let lastScrollTop = 0;

  useEffect(() => {
    if (!alert.clear) {
      setIsLoading(false);
    }
  }, [alert.clear]);

  useEffect(() => {
    dispatch(alertActions.clear());
  }, [dispatch]);

  // Kickoff user load first time, or update with scroll results
  useEffect(() => {
    if (numUsers === 0) {
      setIsLoading(true);
      dispatch(adminActions.getUsers());
    } else {
      setIsLoading(false);
    }
  }, [numUsers, dispatch]);

  useEffect(() => {
    setIsScrollLoading(false);
    setSearchTerm(tempSearchTerm);
  }, [users, tempSearchTerm]);

  const userDetail = (e, row) => {
    if (!alert.clear) {
      dispatch(alertActions.clear());
    }
    setUserId(row.id);
    setMode(DETAIL);
    setIsForm(true);
  };

  const back = () => {
    if (!alert.clear) {
      dispatch(alertActions.clear());
    }
    setMode(LIST);
    setIsForm(false);
  };

  const headers = useMemo(
    () => [
      { Header: '#', accessor: 'order' },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (r) => addBold(r.cell.value, searchTerm),
      },
      {
        Header: 'Email',
        accessor: 'email',
        Cell: (r) => addBold(r.cell.value, searchTerm),
      },
      { Header: 'Athlete', accessor: 'athlete' },
      { Header: 'Coach', accessor: 'coach' },
      { Header: 'Judge', accessor: 'judge' },
      /*{ Header: 'Status', accessor: 'col7' },*/
    ],
    [searchTerm]
  );

  const data = useMemo(() => {
    return users.allIds.map((id, i) => {
      const { name, email, athlete, coach, judgeType /*status*/ } = users.byId[
        id
      ];

      return {
        order: i + 1,
        name: name ? name : '',
        email: email,
        athlete: athlete ? 'Y' : '-',
        coach: coach && coach.items.length > 0 ? 'Y' : '-',
        judge: judgeType && judgeType.length > 0 ? 'Y' : '-',
        /*col7: status,*/
        id: id,
      };
    });
  }, [users]);

  const table = () => {
    const initialState = {
      sortBy: [{ id: 'name', desc: false }],
    };

    const empty = numUsers === 0 || data.length === 0;

    return empty ? (
      noResults('users')
    ) : (
      <TableComponent
        columns={headers}
        data={data}
        textSort={true}
        onClick={userDetail}
        initialState={initialState}
        searchTerm={searchTerm}
        showFooter={true}
        filterColumns={['name', 'email']}
      />
    );
  };

  const handleScroll = (e) => {
    const { scrollHeight, scrollTop, scrollLeft, clientHeight } = e.target;
    const bottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;
    const nextPage =
      (isSearch && searchToken !== null) ||
      (!isSearch && nextToken !== null && numUsers > 0);
    const vertScroll = scrollLeft === 0;

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

    if (bottom && nextPage && vertScroll && !isScrollLoading) {
      setIsScrollLoading(true);
      dispatch(
        adminActions.getUsers({
          searchQuery: searchTerm,
          nextToken: isSearch ? searchToken : nextToken,
        })
      );
    }
  };

  const handleSearch = (query) => {
    setTempSearchTerm(query);

    if (query && query !== '') {
      dispatch(adminActions.getUsers({ searchQuery: query, nextToken: null }));
    } else {
      // clear search
      setSearchTerm(query);
    }
  };

  const adminBody = () => {
    return (
      <>
        {!alert.clear ? (
          <Row style={{ padding: '0 15px', margin: '1rem -15px' }}>
            <Alert
              dismissible
              onClose={() => dispatch(alertActions.clear())}
              variant={alert.type === 'alert-danger' ? 'danger' : 'success'}
              style={{ textAlign: 'center', width: '100%' }}
            >
              {alert.message}
            </Alert>
          </Row>
        ) : null}
        {isLoading ? <Loading /> : table()}
      </>
    );
  };

  const footer = () => {
    return (
      <>
        <Row className="vCenter" style={{ margin: '0 auto' }}>
          <Button
            variant="outline-primary"
            className="formButton"
            onClick={() => handleMode(null)}
            disabled={isLoading}
          >
            Close
          </Button>
        </Row>
        <Row />
      </>
    );
  };

  const headerMsg = () => {
    return <span>User Admin</span>;
  };

  const listModal = () => {
    return (
      <>
        <Modal.Header className="searchHeader">
          {headerMsg()}
          {numUsers !== 0 && data?.length !== 0 ? (
            <SearchInput searchTerm={searchTerm} search={handleSearch} />
          ) : null}
        </Modal.Header>
        <Modal.Body className="setupForm" onScroll={handleScroll}>
          {adminBody()}
          {isScrollLoading ? <ScrollLoading /> : null}
        </Modal.Body>
        <Modal.Footer className="setupFooter">{footer()}</Modal.Footer>
      </>
    );
  };

  const modal = () => {
    switch (mode) {
      case ADD:
        return <UserForm back={back} />;
      case EDIT:
        break;
      case DELETE:
        break;
      case LIST:
        return listModal();
      case DETAIL:
        return <UserDetail back={back} userId={userId} />;
      default:
        return null;
    }
  };

  return modal();
}

export default UserAdmin;
